import { TFunction } from 'i18next';

import { calculateProjectTotal, Demographics, ProjectType } from 'entities';

import { ProjectCreationProps } from './types';

export const demographicsRenderer = [
  { getLabel: (t) => `${t('gender')}: `, key: 'gender' },
  { getLabel: (t) => `${t('age')}: `, key: 'age' },
  { getLabel: (t) => `${t('sel-income')}: `, key: 'selIncome' },
  { getLabel: (t) => `${t('region')}: `, key: 'region' },
  { getLabel: (t) => `${t('house-hold-children')}: `, key: 'houseHoldChildren' },
  { getLabel: (t) => `${t('target-filters')}: `, key: 'targetFilters' },
] as Array<{ getLabel: (t: TFunction) => string; key: keyof Demographics }>;

const typeValidators: {
  [key in ProjectType]?: (config: ProjectCreationProps['typeConfig']) => boolean;
} = {
  TRACKER_ORIGIN: ({ shadowBanInWaves, waves }) => {
    const validType = [shadowBanInWaves, waves].every((value) => typeof value === 'number');

    return validType && (shadowBanInWaves as number) <= (waves as number) && (waves as number) > 0;
  },

  TRACKER_SEQUENCE: ({ originProject }) => {
    return !!originProject;
  },
};

function validateTypeConfig(config: ProjectCreationProps['typeConfig']): boolean {
  const { type } = config;

  if (!type) return false;

  const validator = typeValidators[type as ProjectType];

  return validator?.(config) ?? true;
}

export function validateProjectInfoFilling(project: ProjectCreationProps): boolean {
  const {
    name,
    typeConfig,
    estimatedCompletionWorkingDays,
    client,
    clientOrigin,
    ticket,
    deviceTarget,
    salesPerson,
  } = project;

  const hasNotFilled = [
    name,
    validateTypeConfig(typeConfig),
    estimatedCompletionWorkingDays,
    client,
    clientOrigin,
    ticket,
    deviceTarget,
    salesPerson,
  ].some((field) => !field);

  return hasNotFilled;
}

export function validateServicesFilling(project: ProjectCreationProps): boolean {
  const { services } = project;

  const isNotFilled = !services.length;

  return isNotFilled;
}

export function validateOutsourceFilling(project: ProjectCreationProps): boolean {
  const { services } = project;

  const isNotFilled = services.some(({ outsourceConfig }) =>
    outsourceConfig
      ? Object.values(outsourceConfig).some((value) =>
          typeof value === 'object'
            ? Object.values(value).some((x) => !`${x}`.trim())
            : !value.trim(),
        )
      : false,
  );

  return isNotFilled;
}

function validateCostsFilling(project: ProjectCreationProps): boolean {
  const { countries, services, currency } = project;

  const allGood = [
    currency,

    countries.length,

    Number.isFinite(Number(calculateProjectTotal(services))),

    services.every(({ costs }) =>
      Object.values(costs).every((costs) =>
        costs.every(
          ({ type, value, currency }) => value && (type === 'CURRENCY' ? currency : true),
        ),
      ),
    ),
  ];

  return allGood.some((field) => !field);
}

function validateProjectDetailsFilling(project: ProjectCreationProps): boolean {
  const { teamResponsible, minFee, observations, demographics, countries } = project;

  const hasNotFilled = [
    teamResponsible,

    minFee ? minFee.currency : true,
    minFee ? minFee.value : true,

    observations.bidsNotes,
    observations.clientNotes,
    observations.recruitment ? observations.recruitmentDetails?.trim() : true,
    observations.reprice ? observations.repriceDetails?.trim() : true,

    !!Object.keys(demographics).length &&
      countries.every((country) => {
        const countryDemographic = demographics[country];

        if (!countryDemographic) return false;

        const demographicNotFilled = [
          countryDemographic.age,
          countryDemographic.gender,
          countryDemographic.selIncome,
          countryDemographic.region,
          countryDemographic.houseHoldChildren,
          countryDemographic.targetFilters,
          countryDemographic.quota ? countryDemographic.quotaDetails?.trim() : true,
        ].some((value) => !value);

        return !demographicNotFilled;
      }),
  ].some((field) => !field);

  return hasNotFilled;
}

export const stepBlockers: Record<number, (state: ProjectCreationProps) => boolean> = {
  1: validateProjectInfoFilling,
  2: validateServicesFilling,
  3: validateCostsFilling,
  4: validateOutsourceFilling,
  5: validateProjectDetailsFilling,
};
